/**
 * Timezone Settings Error Handling Utilities
 */

export interface TimezoneError {
	code: string;
	message: string;
	details?: unknown;
}

export const TIMEZONE_ERROR_CODES = {
	API_ERROR: 'TIMEZONE_API_ERROR',
	INVALID_TIMEZONE: 'INVALID_TIMEZONE', 
	NETWORK_ERROR: 'NETWORK_ERROR',
	UNKNOWN_ERROR: 'UNKNOWN_ERROR'
} as const;

export const TIMEZONE_ERROR_MESSAGES = {
	[TIMEZONE_ERROR_CODES.API_ERROR]: 'Failed to save timezone settings. Please try again.',
	[TIMEZONE_ERROR_CODES.INVALID_TIMEZONE]: 'Selected timezone is invalid. Please choose a different timezone.',
	[TIMEZONE_ERROR_CODES.NETWORK_ERROR]: 'Network connection error. Please check your connection and try again.',
	[TIMEZONE_ERROR_CODES.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.'
} as const;

/**
 * Creates a standardized timezone error
 */
export const createTimezoneError = (
	code: keyof typeof TIMEZONE_ERROR_CODES,
	details?: unknown
): TimezoneError => ({
	code: TIMEZONE_ERROR_CODES[code],
	message: TIMEZONE_ERROR_MESSAGES[TIMEZONE_ERROR_CODES[code]],
	details
});

/**
 * Handles and formats API errors for timezone operations
 */
export const handleTimezoneApiError = (error: unknown): TimezoneError => {
	// Network/connection errors
	if (error && typeof error === 'object' && 'name' in error) {
		const errorWithName = error as { name: string };
		if (errorWithName.name === 'NetworkError' || errorWithName.name === 'TypeError') {
			return createTimezoneError('NETWORK_ERROR', error);
		}
	}

	// API errors with message
	if (error && typeof error === 'object' && 'message' in error) {
		return createTimezoneError('API_ERROR', error);
	}

	// Unknown errors
	return createTimezoneError('UNKNOWN_ERROR', error);
};

/**
 * Validates timezone state before API calls
 */
export const validateTimezoneState = (timezone: {
	timezone: string;
	displayName: string;
	abbreviation: string;
}): TimezoneError | null => {
	if (!timezone.timezone || typeof timezone.timezone !== 'string') {
		return createTimezoneError('INVALID_TIMEZONE', 'Timezone value is missing or invalid');
	}

	if (!timezone.displayName || typeof timezone.displayName !== 'string') {
		return createTimezoneError('INVALID_TIMEZONE', 'Timezone display name is missing or invalid');
	}

	if (!timezone.abbreviation || typeof timezone.abbreviation !== 'string') {
		return createTimezoneError('INVALID_TIMEZONE', 'Timezone abbreviation is missing or invalid');
	}

	return null;
};
